import { useMutation } from "@tanstack/react-query";
import { forwardRef, useEffect, useState } from "react";

import { ApiError } from "@/api/client";
import { postNewsletterRequest } from "@/api/newsletter";
import footerWordmark from "@/assets/footer-radar-wordmark.svg";
import footerAaffLogo from "@/assets/footer-aaff-logotype.svg";
import { useMe } from "@/hooks/useAuth";
import { UNSUBSCRIBE_SUCCESS_MESSAGE } from "@/lib/unsubscribe";

import UnsubscribeConfirmDialog from "./UnsubscribeConfirmDialog";

interface Props {
  unsubscribeIntent?: boolean;
  onUnsubscribeIntentConsumed?: () => void;
}

const PulseFooter = forwardRef<HTMLElement, Props>(function PulseFooter(
  { unsubscribeIntent = false, onUnsubscribeIntentConsumed },
  ref,
) {
  const year = new Date().getFullYear();
  const me = useMe();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [dialogSuccess, setDialogSuccess] = useState<string | null>(null);

  const unsubscribeMutation = useMutation({
    mutationFn: (email: string) => postNewsletterRequest(email, "baja"),
    onSuccess: () => {
      setDialogError(null);
      setDialogSuccess(UNSUBSCRIBE_SUCCESS_MESSAGE);
    },
    onError: (err: unknown) => {
      setDialogSuccess(null);
      setDialogError(
        err instanceof ApiError ? err.message : "No se pudo registrar la baja.",
      );
    },
  });

  useEffect(() => {
    if (!unsubscribeIntent || !me.data?.email) return;

    setDialogOpen(true);
    setDialogError(null);
    setDialogSuccess(null);
    onUnsubscribeIntentConsumed?.();
  }, [unsubscribeIntent, me.data?.email, onUnsubscribeIntentConsumed]);

  const openDialog = () => {
    if (!me.data?.email || unsubscribeMutation.isPending) return;
    setDialogError(null);
    setDialogSuccess(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (unsubscribeMutation.isPending) return;
    setDialogOpen(false);
    setDialogError(null);
    setDialogSuccess(null);
  };

  const handleUnsubscribe = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    openDialog();
  };

  const handleConfirm = () => {
    const email = me.data?.email;
    if (!email || unsubscribeMutation.isPending || dialogSuccess) return;
    unsubscribeMutation.mutate(email);
  };

  return (
    <>
      <footer
        ref={ref}
        id="unsubscribe"
        className="pulse-bottom-shell w-full bg-white will-change-transform"
        aria-label="Pie editorial"
      >
        <div className="pulse-bottom pulse-content mx-auto">
          <img
            src={footerWordmark}
            alt="Radar"
            loading="lazy"
            className="pulse-bottom__brand"
          />

          <div className="pulse-bottom__meta">
            <div className="pulse-bottom__copy-row">
              <p className="pulse-bottom__copy m-0">
                El espacio de actualidad de Iberdrola que analiza las señales clave del día en publicidad, medios e inteligencia artificial, explorando cómo tecnología, creatividad y ética están redefiniendo la relación entre marcas y audiencias.
              </p>

              <div className="pulse-bottom__links">
                <a
                  href="#unsubscribe"
                  className="pulse-bottom__unsubscribe"
                  onClick={handleUnsubscribe}
                  aria-disabled={!me.data?.email || unsubscribeMutation.isPending}
                >
                  {unsubscribeMutation.isPending ? "Registrando…" : "Darse de baja"}
                </a>
                <p className="pulse-bottom__year m-0">
                  ©{year}
                </p>
              </div>
            </div>

            <img
              src={footerAaffLogo}
              alt="AAFF"
              loading="lazy"
              width={197}
              height={70}
              className="pulse-bottom__iberdrola"
            />
          </div>
        </div>
      </footer>

      <UnsubscribeConfirmDialog
        open={dialogOpen}
        isSubmitting={unsubscribeMutation.isPending}
        errorMessage={dialogError}
        successMessage={dialogSuccess}
        onConfirm={handleConfirm}
        onClose={closeDialog}
      />
    </>
  );
});

export default PulseFooter;
